<script setup lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { useToast } from '@/components/ui/toast'
  import { useTranslation } from '@/composables/useTranslation'
  import { formatDateGerman } from '@/lib/util/formatting'
  import type { DocumentHistoryEntry } from '@/types/generated'
  import { DocumentsService } from '@/types/generated/services.gen'
  import { Eye, Loader } from 'lucide-vue-next'
  import { ref, watch } from 'vue'

  type HistoryEntry = DocumentHistoryEntry & { uuid: string }

  const props = defineProps<{
    open: boolean
    documentUuid: string
    documentName: string
  }>()

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
    (e: 'restored'): void
    (e: 'preview', entry: HistoryEntry): void
  }>()

  const { toast } = useToast()
  const { t } = useTranslation({
    en: {
      title: 'Document history',
      description:
        'Versions this document was produced from. Restore brings the selected version back as a live document — "{name}" stays as it is.',
      loading: 'Loading history…',
      empty: 'This document has no prior versions.',
      pages: 'No prior pages | 1 page | {count} pages',
      restorableUntil: 'restorable until {at}',
      expired: 'restore window expired',
      restore: 'Restore',
      preview: 'Preview',
      close: 'Close',
      restoreSuccess: 'Document restored',
      restoreError: 'Could not restore',
      loadError: 'Could not load document history'
    },
    de: {
      title: 'Dokumentenverlauf',
      description:
        'Versionen, aus denen dieses Dokument entstanden ist. „Wiederherstellen" stellt die ausgewählte Version als eigenständiges Dokument wieder her — „{name}" bleibt unverändert.',
      loading: 'Verlauf wird geladen…',
      empty: 'Dieses Dokument hat keine früheren Versionen.',
      pages: 'Keine vorherigen Seiten | 1 Seite | {count} Seiten',
      restorableUntil: 'wiederherstellbar bis {at}',
      expired: 'Wiederherstellungszeitraum abgelaufen',
      restore: 'Wiederherstellen',
      preview: 'Vorschau',
      close: 'Schließen',
      restoreSuccess: 'Dokument wiederhergestellt',
      restoreError: 'Wiederherstellung fehlgeschlagen',
      loadError: 'Dokumentenverlauf konnte nicht geladen werden'
    }
  })

  const loading = ref(false)
  const ancestors = ref<HistoryEntry[]>([])
  const restoringUuid = ref<string | null>(null)

  const canPreview = (entry: HistoryEntry): boolean => !!entry.current_pdf_url

  const fetchHistory = async () => {
    loading.value = true
    try {
      const data = await DocumentsService.documentsHistoryRetrieve({ uuid: props.documentUuid })
      ancestors.value = (data.ancestors || []).filter((a): a is HistoryEntry => !!a.uuid)
    } catch (error: any) {
      ancestors.value = []
      toast({
        title: t('loadError'),
        description: error?.body?.error || error?.message,
        variant: 'destructive'
      })
    } finally {
      loading.value = false
    }
  }

  const handleRestore = async (ancestor: HistoryEntry) => {
    if (restoringUuid.value !== null) return
    restoringUuid.value = ancestor.uuid
    try {
      await DocumentsService.documentsRestoreCreate({ uuid: ancestor.uuid })
      toast({
        title: t('restoreSuccess'),
        variant: 'default'
      })
      emit('restored')
      handleOpenChange(false)
    } catch (error: any) {
      toast({
        title: t('restoreError'),
        description: error?.body?.error || error?.message,
        variant: 'destructive'
      })
    } finally {
      restoringUuid.value = null
    }
  }

  const handleOpenChange = (value: boolean) => {
    emit('update:open', value)
  }

  watch(
    () => [props.open, props.documentUuid] as const,
    ([isOpen, uuid]) => {
      if (isOpen && uuid) fetchHistory()
    },
    { immediate: true }
  )
</script>

<template>
  <Dialog
    :open="open"
    @update:open="handleOpenChange"
  >
    <DialogContent class="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{{ t('title') }}</DialogTitle>
        <DialogDescription>{{ t('description', { name: documentName }) }}</DialogDescription>
      </DialogHeader>

      <div
        v-if="loading"
        class="py-6 text-center text-sm text-gray-500"
      >
        {{ t('loading') }}
      </div>

      <div
        v-else-if="ancestors.length === 0"
        class="py-6 text-center text-sm text-gray-500"
      >
        {{ t('empty') }}
      </div>

      <ul
        v-else
        class="divide-y divide-gray-200"
      >
        <li
          v-for="ancestor in ancestors"
          :key="ancestor.uuid"
          class="flex items-center justify-between gap-4 py-3"
        >
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium text-gray-900">
              {{ ancestor.title || ancestor.filename }}
            </p>
            <p class="text-xs text-gray-500">
              {{ t('pages', { count: ancestor.page_count }) }}
              <template v-if="ancestor.deleted_at">
                · {{ formatDateGerman(ancestor.deleted_at) }}
              </template>
            </p>
            <p
              v-if="ancestor.is_deleted"
              class="text-xs"
              :class="ancestor.is_restorable ? 'text-amber-600' : 'text-red-600'"
            >
              <template v-if="ancestor.is_restorable && ancestor.restorable_until">
                {{ t('restorableUntil', { at: formatDateGerman(ancestor.restorable_until) }) }}
              </template>
              <template v-else>
                {{ t('expired') }}
              </template>
            </p>
          </div>
          <div class="flex items-center gap-2">
            <Button
              v-if="canPreview(ancestor)"
              size="sm"
              variant="ghost"
              :title="t('preview')"
              @click="emit('preview', ancestor)"
            >
              <Eye class="h-4 w-4" />
            </Button>
            <Button
              v-if="ancestor.is_deleted"
              size="sm"
              :disabled="!ancestor.is_restorable || restoringUuid !== null"
              @click="handleRestore(ancestor)"
            >
              <Loader
                v-if="restoringUuid === ancestor.uuid"
                class="mr-1 h-3.5 w-3.5 animate-spin"
              />
              {{ t('restore') }}
            </Button>
          </div>
        </li>
      </ul>

      <DialogFooter>
        <Button
          variant="secondary"
          @click="handleOpenChange(false)"
        >
          {{ t('close') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
